import React, { useEffect, useLayoutEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import { parse } from "query-string"
import { useTranslation } from "react-i18next"
import clsx from "clsx"

import KfLogo from "./KfLogo"
import KfList from "./KfList"
import { getCategoriesThatHaveFood, isDirectionLtr } from "utils"
import KfSubCategories from "./KfSubCategories"
import KfCategoriesSidebar from "./KfCategoriesSidebar"
import KfFooter from "./KfFooter"
import { getLayout } from "services"
import {
  getStoreDesignStyle,
  mergeStoreDesign
} from "utils/storeDesign"
import classes from "./index.module.scss"

const KioskFoods = () => {
  const categories =
    useSelector(state => state.categories)

  const branchId =
    useSelector(state => state.userAccountInfo.branchId)

  const foodsMap =
    useSelector(state => state.foods)

  const groupedFoodIdsBasedOnCategory =
    useSelector(state => state.groupedFoodIdsBasedOnCategory)

  const deliveryType =
    useSelector(state => state.deliveryInfo.deliveryType)

  const location = useLocation()

  const i18n = useTranslation()[1]

  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedSubCategory, setSelectedSubCategory] = useState(null)
  const [storeDesign, setStoreDesign] = useState(mergeStoreDesign())

  const filteredCategories =
    useMemo(() => {
      const categoriesThatHaveFood =
        getCategoriesThatHaveFood(
          categories,
          branchId,
          deliveryType,
          foodsMap,
          groupedFoodIdsBasedOnCategory
        )

      return categoriesThatHaveFood
    }, [
      categories,
      branchId,
      deliveryType,
      foodsMap,
      groupedFoodIdsBasedOnCategory
    ])

  const setInitialSelectedCategory = () => {
    if (filteredCategories.length === 0) return

    if (selectedCategory !== null) return

    const queries = parse(location.search)

    if (queries.categoryId) {
      const category =
        filteredCategories.find(category =>
          category._id === queries.categoryId
        )

      if (!category) return

      setSelectedCategory(
        category
      )

      return
    }

    if (!filteredCategories[0]) return

    setSelectedCategory(
      filteredCategories[0]
    )
  }

  const setInitialSelectedSubCategory = () => {
    if (filteredCategories.length === 0) return

    if (selectedSubCategory !== null) return

    const queries = parse(location.search)

    if (!queries.subCategoryId) return

    const category =
      categories.find(category =>
        category._id === queries.subCategoryId
      )

    if (!category) return

    setSelectedSubCategory(
      category
    )
  }

  useLayoutEffect(() => {
    setInitialSelectedCategory()
  }, [filteredCategories])

  useLayoutEffect(() => {
    setInitialSelectedSubCategory()
  }, [filteredCategories])

  useEffect(() => {
    const getKioskDesign = async () => {
      try {
        const layout = await getLayout()
        const landingLayout = layout.landing || {}

        setStoreDesign(
          mergeStoreDesign(landingLayout.design)
        )
      } catch (err) {
        console.log(err)
      }
    }

    getKioskDesign()
  }, [])

  const storeDesignStyle =
    getStoreDesignStyle(storeDesign)

  return (
    <div
      className={classes["container"]}
      style={storeDesignStyle}
    >
      <KfLogo />

      <KfFooter />

      <div className={classes["main"]}>
        <KfCategoriesSidebar
          filteredCategories={filteredCategories}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          setSelectedSubCategory={setSelectedSubCategory}
        />

        <div
          className={clsx(classes["right"],
            isDirectionLtr(i18n.language) && classes["right-ltr"]
          )}
        >

          <KfSubCategories
            filteredCategories={filteredCategories}
            selectedCategory={selectedCategory}
            selectedSubCategory={selectedSubCategory}
            setSelectedSubCategory={setSelectedSubCategory}
          />

          <KfList
            selectedCategory={selectedCategory}
            selectedSubCategory={selectedSubCategory}
            design={storeDesign}
          />
        </div>
      </div>
    </div>
  )
}

export default KioskFoods
